// Ported from exams presentation.tsx HelpNote (winner — the libms LEVEL_GUIDE variant was domain-coupled and is dead); palette tones (sky/amber/emerald) → semantic info/warning/success/neutral tints, radius → rounded-500 (the inline-note step, DEV-GUIDE-002 §12.1), role=note added; visibility gated by the shared help-mode signal.
import { Show, createSignal, type JSX } from 'solid-js';
import { createIsNarrow } from '../breakpoint/index.js';
import { CircleCheck, Info, TriangleAlert } from 'lucide-solid';
import { helpMode } from '../help-mode/index.js';

export type HelpTone = 'info' | 'warning' | 'success' | 'neutral';

/* Status tones are Spectrum two-tone (opaque 100-tint ground, role ink) and FLAT — no
   border. Only the neutral tone keeps a decorative gray border, because base-200 alone
   would not separate it from a base-200 ground. */
const HELP_TONE: Record<HelpTone, { wrap: string; icon: string; glyph: (s: number) => JSX.Element }> = {
  info: { wrap: 'bg-info-tint', icon: 'text-info', glyph: (s) => <Info size={s} /> },
  warning: { wrap: 'bg-warning-tint', icon: 'text-warning', glyph: (s) => <TriangleAlert size={s} /> },
  success: { wrap: 'bg-success-tint', icon: 'text-success', glyph: (s) => <CircleCheck size={s} /> },
  neutral: { wrap: 'border border-base-300 bg-base-200', icon: 'text-base-subtle', glyph: (s) => <Info size={s} /> },
};

/** Inline contextual callout — hidden while help mode is off, unless `force`d. */
export function HelpNote(props: {
  tone?: HelpTone;
  title?: string;
  /** Override the leading icon. */
  icon?: JSX.Element;
  /** Always render, ignoring the help-mode toggle (onboarding-critical guidance). */
  force?: boolean;
  children: JSX.Element;
  class?: string;
}) {
  const t = () => HELP_TONE[props.tone ?? 'info'];
  /* THREE LINES ON A PHONE, the rest behind "More" (2026-10-01, measured on
     stage at 390px). These notes are long by design — the verification queue's
     runs eleven lines, the authors register's twenty — and at full length the
     note took 640px of an 844px screen, so a register whose whole job is to
     list records opened on two screens of prose with the first record below
     the fold. Nothing is removed: the toggle is one tap and the text is in the
     DOM either way, so find-in-page and a screen reader still reach it.

     From `sm` up this is exactly as it was. The jsdom fallback is the wide
     branch (matchMedia is undefined there), so no suite sees a clamp. */
  const narrow = createIsNarrow();
  const [expanded, setExpanded] = createSignal(false);
  const clamped = () => narrow() && !expanded();
  return (
    <Show when={props.force || helpMode()}>
      <div role="note" class={`flex items-start gap-2.5 rounded-500 p-3 ${t().wrap} ${props.class ?? ''}`}>
        <span class={`mt-0.5 flex shrink-0 ${t().icon}`} aria-hidden="true">
          <Show when={props.icon} fallback={t().glyph(15)}>
            {props.icon}
          </Show>
        </span>
        <div class="min-w-0 flex-1 text-xs leading-relaxed text-base-muted">
          <Show when={props.title}>
            <div class="mb-0.5 font-bold text-base-muted">{props.title}</div>
          </Show>
          <div class={clamped() ? 'line-clamp-3' : ''}>{props.children}</div>
          <Show when={narrow()}>
            {/* Accent ink, which §12.4 allows (accent and negative only) — and
                a button, not a link: it changes this box, it does not navigate. */}
            <button
              type="button"
              class="mt-1 text-xs font-medium text-primary"
              aria-expanded={expanded()}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded() ? 'Less' : 'More'}
            </button>
          </Show>
        </div>
      </div>
    </Show>
  );
}
